import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Save,
  Camera,
  MapPin,
  Phone,
  Mail,
  Globe,
  Clock,
  Star,
  MessageSquare,
  BarChart3,
  Settings,
  Image,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Wifi,
  CreditCard,
  Car,
  ParkingCircle,
  CheckCircle,
  Plus,
  X,
  Upload,
  Loader2,
  ArrowLeft,
  Edit3,
  Trash2,
  Send,
  TrendingUp
} from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

const BusinessOwnerPanel = ({ onBack }) => {
  const { darkMode } = useTheme();
  const { user, token } = useAuth();
  const [activeTab, setActiveTab] = useState('info');
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [replyText, setReplyText] = useState({});
  const galleryInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-gray-50';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';
  const inputBg = darkMode ? 'bg-slate-700' : 'bg-gray-100';

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    short_description: '',
    image: '',
    gallery: [],
    address: '',
    phone: '',
    email: '',
    website: '',
    maps_url: '',
    latitude: '',
    longitude: '',
    price_range: '$$',
    features: [],
    working_hours: {
      monday: { open: '09:00', close: '18:00', closed: false },
      tuesday: { open: '09:00', close: '18:00', closed: false },
      wednesday: { open: '09:00', close: '18:00', closed: false },
      thursday: { open: '09:00', close: '18:00', closed: false },
      friday: { open: '09:00', close: '18:00', closed: false },
      saturday: { open: '10:00', close: '16:00', closed: false },
      sunday: { open: '', close: '', closed: true },
    },
    facebook: '',
    instagram: '',
    twitter: '',
    youtube: ''
  });

  const dayNames = {
    monday: 'Pazartesi',
    tuesday: 'Salı',
    wednesday: 'Çarşamba',
    thursday: 'Perşembe',
    friday: 'Cuma',
    saturday: 'Cumartesi',
    sunday: 'Pazar'
  };

  const featureOptions = [
    { value: 'wifi', label: 'WiFi', icon: Wifi },
    { value: 'credit_card', label: 'Kredi Kartı', icon: CreditCard },
    { value: 'parking', label: 'Otopark', icon: ParkingCircle },
    { value: 'wheelchair', label: 'Engelli Erişimi', icon: CheckCircle },
  ];

  const tabs = [
    { id: 'info', label: 'Bilgiler', icon: Building2 },
    { id: 'reviews', label: 'Yorumlar', icon: MessageSquare },
    { id: 'stats', label: 'İstatistikler', icon: BarChart3 },
    { id: 'gallery', label: 'Galeri', icon: Image },
  ];

  useEffect(() => {
    fetchBusiness();
  }, []);

  const fetchBusiness = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-business`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBusiness(data);
        setFormData({
          name: data.name || '',
          description: data.description || '',
          short_description: data.short_description || '',
          image: data.image || '',
          gallery: data.gallery || [],
          address: data.address || '',
          phone: data.phone || '',
          email: data.email || '',
          website: data.website || '',
          maps_url: data.maps_url || '',
          latitude: data.latitude || '',
          longitude: data.longitude || '',
          price_range: data.price_range || '$$',
          features: data.features || [],
          working_hours: data.working_hours || formData.working_hours,
          facebook: data.social_media?.facebook || '',
          instagram: data.social_media?.instagram || '',
          twitter: data.social_media?.twitter || '',
          youtube: data.social_media?.youtube || ''
        });
        fetchReviews();
        fetchStats();
      }
    } catch (error) {
      console.error('Error fetching business:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-business/reviews`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReviews(data.reviews || []);
      }
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/my-business/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/my-business`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        alert('Değişiklikler kaydedildi!');
        fetchBusiness();
      } else {
        const error = await response.json();
        alert('Hata: ' + (error.detail || 'Kaydetme başarısız'));
      }
    } catch (error) {
      console.error('Error saving:', error);
      alert('Kaydetme sırasında bir hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const handleReply = async (reviewId) => {
    const reply = replyText[reviewId];
    if (!reply?.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/my-business/reviews/${reviewId}/reply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ reply })
      });

      if (response.ok) {
        setReplyText({ ...replyText, [reviewId]: '' });
        fetchReviews();
      }
    } catch (error) {
      console.error('Error replying:', error);
    }
  };

  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    for (const file of files) {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      try {
        const response = await fetch(`${API_URL}/api/upload`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formDataUpload
        });

        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            gallery: [...prev.gallery, data.url]
          }));
        }
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }));
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center`}>
        <Loader2 className={`w-8 h-8 animate-spin ${textSecondary}`} />
      </div>
    );
  }

  if (!business) {
    return (
      <div className={`min-h-screen ${bgMain} flex items-center justify-center p-4`}>
        <div className={`${bgCard} rounded-2xl p-8 text-center max-w-md`}>
          <Building2 className={`w-16 h-16 mx-auto mb-4 ${textSecondary}`} />
          <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>İşletmeniz Yok</h2>
          <p className={`${textSecondary} mb-4`}>
            Henüz size atanmış bir işletme bulunmuyor. Bir işletme talep edebilir veya admin ile iletişime geçebilirsiniz.
          </p>
          <button
            onClick={onBack}
            className="px-6 py-2 bg-emerald-500 text-white rounded-xl font-medium"
          >
            Geri Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bgMain} pb-24`}>
      {/* Header */}
      <div className={`${bgCard} border-b ${borderColor} px-4 py-4`}>
        <div className="max-w-3xl mx-auto flex items-center gap-4">
          <button onClick={onBack} className={`p-2 rounded-lg ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}>
            <ArrowLeft className={`w-5 h-5 ${textPrimary}`} />
          </button>
          <div className="flex-1">
            <h1 className={`text-lg font-bold ${textPrimary}`}>İşletme Yönetimi</h1>
            <p className={`text-sm ${textSecondary}`}>{business.name}</p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-emerald-500 text-white rounded-xl font-medium flex items-center gap-2 disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Kaydet
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className={`${bgCard} border-b ${borderColor}`}>
        <div className="max-w-3xl mx-auto flex">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 py-3 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'text-emerald-500 border-b-2 border-emerald-500'
                    : textSecondary
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto p-4">
        {activeTab === 'info' && (
          <div className="space-y-4">
            {/* Basic Info */}
            <div className={`${bgCard} rounded-2xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-4`}>Temel Bilgiler</h3>
              <div className="space-y-4">
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>İşletme Adı</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>Kısa Açıklama</label>
                  <input
                    type="text"
                    value={formData.short_description}
                    onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>Detaylı Açıklama</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={4}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary} resize-none`}
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className={`${bgCard} rounded-2xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-4`}>İletişim Bilgileri</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>Telefon</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>E-posta</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>Website</label>
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary}`}
                  />
                </div>
                <div>
                  <label className={`text-sm ${textSecondary} mb-1 block`}>Adres</label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className={`w-full px-4 py-2 ${inputBg} rounded-xl ${textPrimary}`}
                  />
                </div>
              </div>
            </div>

            {/* Working Hours */}
            <div className={`${bgCard} rounded-2xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-4`}>Çalışma Saatleri</h3>
              <div className="space-y-3">
                {Object.entries(dayNames).map(([day, label]) => (
                  <div key={day} className="flex items-center gap-4">
                    <span className={`w-24 text-sm ${textPrimary}`}>{label}</span>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!formData.working_hours[day]?.closed}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          working_hours: {
                            ...prev.working_hours,
                            [day]: { ...prev.working_hours[day], closed: !e.target.checked }
                          }
                        }))}
                        className="rounded"
                      />
                      <span className={`text-xs ${textSecondary}`}>Açık</span>
                    </label>
                    {!formData.working_hours[day]?.closed && (
                      <>
                        <input
                          type="time"
                          value={formData.working_hours[day]?.open || '09:00'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            working_hours: {
                              ...prev.working_hours,
                              [day]: { ...prev.working_hours[day], open: e.target.value }
                            }
                          }))}
                          className={`px-2 py-1 ${inputBg} rounded text-sm ${textPrimary}`}
                        />
                        <span className={textSecondary}>-</span>
                        <input
                          type="time"
                          value={formData.working_hours[day]?.close || '18:00'}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            working_hours: {
                              ...prev.working_hours,
                              [day]: { ...prev.working_hours[day], close: e.target.value }
                            }
                          }))}
                          className={`px-2 py-1 ${inputBg} rounded text-sm ${textPrimary}`}
                        />
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div className={`${bgCard} rounded-2xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-4`}>Özellikler</h3>
              <div className="flex flex-wrap gap-2">
                {featureOptions.map((feature) => {
                  const Icon = feature.icon;
                  const isActive = formData.features.includes(feature.value);
                  return (
                    <button
                      key={feature.value}
                      onClick={() => toggleFeature(feature.value)}
                      className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-colors ${
                        isActive
                          ? 'bg-emerald-500 text-white'
                          : `${inputBg} ${textPrimary}`
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {feature.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Social Media */}
            <div className={`${bgCard} rounded-2xl p-4`}>
              <h3 className={`font-semibold ${textPrimary} mb-4`}>Sosyal Medya</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Facebook className="w-5 h-5 text-blue-600" />
                  <input
                    type="url"
                    placeholder="Facebook URL"
                    value={formData.facebook}
                    onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                    className={`flex-1 px-3 py-2 ${inputBg} rounded-xl text-sm ${textPrimary}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Instagram className="w-5 h-5 text-pink-600" />
                  <input
                    type="url"
                    placeholder="Instagram URL"
                    value={formData.instagram}
                    onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                    className={`flex-1 px-3 py-2 ${inputBg} rounded-xl text-sm ${textPrimary}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Twitter className="w-5 h-5 text-sky-500" />
                  <input
                    type="url"
                    placeholder="Twitter URL"
                    value={formData.twitter}
                    onChange={(e) => setFormData({ ...formData, twitter: e.target.value })}
                    className={`flex-1 px-3 py-2 ${inputBg} rounded-xl text-sm ${textPrimary}`}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Youtube className="w-5 h-5 text-red-600" />
                  <input
                    type="url"
                    placeholder="YouTube URL"
                    value={formData.youtube}
                    onChange={(e) => setFormData({ ...formData, youtube: e.target.value })}
                    className={`flex-1 px-3 py-2 ${inputBg} rounded-xl text-sm ${textPrimary}`}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className={`${bgCard} rounded-2xl p-8 text-center`}>
                <MessageSquare className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
                <h3 className={`font-semibold ${textPrimary} mb-2`}>Henüz Yorum Yok</h3>
                <p className={`text-sm ${textSecondary}`}>İşletmenize henüz yorum yapılmamış.</p>
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className={`${bgCard} rounded-2xl p-4`}>
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full ${inputBg} flex items-center justify-center`}>
                      <span className={`text-sm font-medium ${textPrimary}`}>
                        {review.user_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className={`font-medium ${textPrimary}`}>{review.user_name}</h4>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className={`text-xs ${textSecondary} mb-2`}>
                        {new Date(review.created_at).toLocaleDateString('tr-TR')}
                      </p>
                      <p className={`text-sm ${textPrimary}`}>{review.comment}</p>

                      {/* Existing Reply */}
                      {review.reply && (
                        <div className={`mt-3 p-3 ${inputBg} rounded-xl`}>
                          <p className={`text-xs font-medium ${textSecondary} mb-1`}>Yanıtınız:</p>
                          <p className={`text-sm ${textPrimary}`}>{review.reply}</p>
                        </div>
                      )}

                      {/* Reply Form */}
                      {!review.reply && (
                        <div className="mt-3 flex gap-2">
                          <input
                            type="text"
                            placeholder="Yanıt yaz..."
                            value={replyText[review.id] || ''}
                            onChange={(e) => setReplyText({ ...replyText, [review.id]: e.target.value })}
                            className={`flex-1 px-3 py-2 ${inputBg} rounded-xl text-sm ${textPrimary}`}
                          />
                          <button
                            onClick={() => handleReply(review.id)}
                            className="px-4 py-2 bg-emerald-500 text-white rounded-xl"
                          >
                            <Send className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="space-y-4">
            {stats ? (
              <>
                {/* Overview */}
                <div className="grid grid-cols-2 gap-4">
                  <div className={`${bgCard} rounded-2xl p-4 text-center`}>
                    <Star className="w-8 h-8 mx-auto mb-2 text-yellow-500" />
                    <p className={`text-3xl font-bold ${textPrimary}`}>{stats.average_rating}</p>
                    <p className={`text-sm ${textSecondary}`}>Ortalama Puan</p>
                  </div>
                  <div className={`${bgCard} rounded-2xl p-4 text-center`}>
                    <MessageSquare className="w-8 h-8 mx-auto mb-2 text-emerald-500" />
                    <p className={`text-3xl font-bold ${textPrimary}`}>{stats.total_reviews}</p>
                    <p className={`text-sm ${textSecondary}`}>Toplam Yorum</p>
                  </div>
                </div>

                {/* Rating Distribution */}
                <div className={`${bgCard} rounded-2xl p-4`}>
                  <h3 className={`font-semibold ${textPrimary} mb-4`}>Puan Dağılımı</h3>
                  <div className="space-y-2">
                    {[5, 4, 3, 2, 1].map((rating) => {
                      const count = stats.rating_distribution[rating] || 0;
                      const percentage = stats.total_reviews > 0 ? (count / stats.total_reviews) * 100 : 0;
                      return (
                        <div key={rating} className="flex items-center gap-3">
                          <span className={`w-8 text-sm ${textPrimary}`}>{rating} ★</span>
                          <div className={`flex-1 h-4 ${inputBg} rounded-full overflow-hidden`}>
                            <div
                              className="h-full bg-yellow-500 rounded-full"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className={`w-8 text-sm ${textSecondary}`}>{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            ) : (
              <div className={`${bgCard} rounded-2xl p-8 text-center`}>
                <BarChart3 className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
                <p className={`${textSecondary}`}>İstatistikler yükleniyor...</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'gallery' && (
          <div className="space-y-4">
            <div className={`${bgCard} rounded-2xl p-4`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold ${textPrimary}`}>Fotoğraf Galerisi</h3>
                <button
                  onClick={() => galleryInputRef.current?.click()}
                  className="px-4 py-2 bg-emerald-500 text-white rounded-xl flex items-center gap-2"
                >
                  <Upload className="w-4 h-4" />
                  Fotoğraf Ekle
                </button>
                <input
                  ref={galleryInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleGalleryUpload}
                  className="hidden"
                />
              </div>

              {formData.gallery.length > 0 ? (
                <div className="grid grid-cols-3 gap-2">
                  {formData.gallery.map((img, index) => (
                    <div key={index} className="relative aspect-square rounded-xl overflow-hidden group">
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeGalleryImage(index)}
                        className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`border-2 border-dashed ${borderColor} rounded-xl p-8 text-center`}>
                  <Camera className={`w-12 h-12 mx-auto mb-4 ${textSecondary}`} />
                  <p className={`${textSecondary}`}>Henüz fotoğraf eklenmemiş</p>
                  <p className={`text-sm ${textSecondary}`}>Fotoğraf eklemek için yukarıdaki butonu kullanın</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessOwnerPanel;
